import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  tenantRbacApi,
  type TenantMenuCatalogResponse,
  type TenantPermissionDef,
  type TenantRoleRow,
} from '@/api/tenant-rbac';
import type { NavMenuNode } from '@/api/platform-nav-menu';
import { RoleMenuCheckboxes } from '@/components/rbac/RoleMenuCheckboxes';
import {
  RoleMenuTreeCheckboxes,
  filterNavTreeForRolePicker,
} from '@/components/rbac/RoleMenuTreeCheckboxes';
import { refreshSessionFromServer } from '@/lib/session-sync';
import { useTenantPermission } from '@/hooks/use-tenant-permission';
import { useUIStore } from '@/store/useUIStore';
import { toast } from 'sonner';
import {
  adminDrawerFooterClass,
  adminDrawerHeaderClass,
  adminDrawerShell,
  btnOutlineNeon,
  btnPrimaryNeon,
} from '@/lib/admin-ui';
import { cn } from '@/lib/utils';

/** 租户侧 Drawer 表单：与平台页一致，避免 disabled Input 发灰不可读 */
const drawerInputClass =
  'border-white/10 bg-zinc-900/60 text-zinc-100 placeholder:text-zinc-500 focus-visible:ring-fuchsia-500/35 disabled:cursor-not-allowed disabled:opacity-100 disabled:bg-zinc-900/95 disabled:text-zinc-300';
const drawerLabelClass = 'text-zinc-200';

const BUILTIN_LABELS = new Set(['租户管理员', '租户成员']);

/** 种子内置「租户管理员」角色 id（与 prisma/seed 一致） */
const TENANT_ROLE_ADMIN_ID = 'role-tenant-admin';
const FEATURE_PERMISSION_CODE_MAP: Record<string, string[]> = {
  'workspace.view': ['tenant:workspace:view'],
  'market.view': ['tenant:market:view'],
  'team.view': ['tenant:user:view'],
  'team.manage': ['tenant:user:manage', 'tenant:user:view'],
  'roles.view': ['tenant:user:view'],
  'roles.manage': ['tenant:user:manage', 'tenant:user:view'],
  'audit.view': ['tenant:user:view'],
};

function getViewDependency(code: string): string | null {
  if (!code.endsWith(':manage')) return null;
  const base = code.slice(0, -':manage'.length);
  return `${base}:view`;
}

function deriveFeaturePermissionKeys(codes: string[]): string[] {
  const codeSet = new Set(codes);
  return Object.entries(FEATURE_PERMISSION_CODE_MAP)
    .filter(([, required]) => required.every((c) => codeSet.has(c)))
    .map(([key]) => key);
}

function derivePermissionCodesFromMenuPaths(
  menuPaths: Set<string>,
  menuCatalog: TenantMenuCatalogResponse['items'],
) {
  const out = new Set<string>();
  [...menuPaths].forEach((path) => {
    const item = menuCatalog.find((m) => m.path === path);
    (item?.permissions ?? []).forEach((p) => out.add(p));
  });
  if (menuPaths.size > 0) {
    out.add('tenant:inbox:view');
  }
  return [...out];
}

function inferMenuPathsFromPermissionCodes(
  codes: Set<string>,
  menuCatalog: TenantMenuCatalogResponse['items'],
) {
  const out = new Set<string>();
  menuCatalog.forEach((item) => {
    if ((item.permissions ?? []).some((p) => codes.has(p))) {
      out.add(item.path);
    }
  });
  return out;
}

const FEATURE_PERMISSION_LABEL_MAP: Record<string, string> = {
  'workspace.view': '工作台 - 查看',
  'market.view': '应用市场 - 查看',
  'team.view': '成员与账号 - 查看',
  'team.manage': '成员与账号 - 管理',
  'roles.view': '角色与权限 - 查看',
  'roles.manage': '角色与权限 - 管理',
  'audit.view': '审计日志 - 查看',
};

export function TenantRolesPage() {
  const canManage = useTenantPermission('tenant:user:manage');
  const hasRoleView = useTenantPermission('tenant:user:view');
  const isLight = useUIStore((s) => s.theme === 'light');
  const lightPrimaryBtn =
    'border border-cyan-500 bg-cyan-600 text-white hover:bg-cyan-500';
  const lightOutlineBtn =
    'border border-slate-300 bg-white text-slate-700 hover:border-cyan-400/45 hover:bg-cyan-50 hover:text-cyan-700';
  const lightDrawerField =
    'border-slate-300 bg-white text-slate-800 placeholder:text-slate-400 focus-visible:ring-cyan-500/35 disabled:bg-slate-100 disabled:text-slate-500';
  const lightDrawerLabel = 'text-slate-700';
  /** 仅有查看权限：可看列表与详情弹窗，不可新建/编辑/删除 */
  const readOnly = hasRoleView && !canManage;

  const [defs, setDefs] = useState<TenantPermissionDef[]>([]);
  const [roles, setRoles] = useState<TenantRoleRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [editRole, setEditRole] = useState<TenantRoleRow | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [viewRole, setViewRole] = useState<TenantRoleRow | null>(null);

  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formMenuPaths, setFormMenuPaths] = useState<Set<string>>(new Set());
  const [menuCatalog, setMenuCatalog] = useState<
    TenantMenuCatalogResponse['items']
  >([]);
  const [menuTreeForPicker, setMenuTreeForPicker] = useState<NavMenuNode[] | null>(
    null,
  );
  const [saving, setSaving] = useState(false);
  const [expandedRoleIds, setExpandedRoleIds] = useState<Set<string>>(new Set());
  const menuDrivenCodes = useMemo(() => {
    const raw = derivePermissionCodesFromMenuPaths(formMenuPaths, menuCatalog);
    return normalizeCodesWithDependencies(new Set(raw));
  }, [formMenuPaths, menuCatalog]);
  const manageWithoutViewCount = useMemo(() => {
    let count = 0;
    menuDrivenCodes.forEach((code) => {
      const dep = getViewDependency(code);
      if (dep && !menuDrivenCodes.has(dep)) count += 1;
    });
    return count;
  }, [menuDrivenCodes]);

  const menuConflictPaths = [...formMenuPaths].filter((path) => {
    const item = menuCatalog.find((m) => m.path === path);
    if (!item || !item.permissions?.length) return false;
    return !item.permissions.some((perm) => menuDrivenCodes.has(perm));
  });

  const permissionSummaryCount = menuDrivenCodes.size;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      if (readOnly) {
        const r = await tenantRbacApi.listRoles();
        setRoles(r.data);
        setDefs([]);
        setMenuCatalog([]);
        setMenuTreeForPicker(null);
      } else {
        const [p, r, cat, rawTree] = await Promise.all([
          tenantRbacApi.listPermissions(),
          tenantRbacApi.listRoles(),
          tenantRbacApi.getMenuCatalog(),
          tenantRbacApi.getMenuTree().catch(() => null as NavMenuNode[] | null),
        ]);
        setDefs(p.data);
        setRoles(r.data);
        setMenuCatalog(cat.data.items);
        if (rawTree?.length) {
          const filtered = filterNavTreeForRolePicker(rawTree);
          setMenuTreeForPicker(filtered.length > 0 ? filtered : null);
        } else {
          setMenuTreeForPicker(null);
        }
      }
    } catch {
      toast.error('加载角色与权限失败');
    } finally {
      setLoading(false);
    }
  }, [readOnly]);

  useEffect(() => {
    void load();
  }, [load]);

  function openCreate() {
    setFormName('');
    setFormDesc('');
    setFormMenuPaths(new Set());
    setCreateOpen(true);
  }

  function openEdit(role: TenantRoleRow) {
    if (role.id === TENANT_ROLE_ADMIN_ID) {
      toast.info('内置租户管理员角色不可编辑');
      return;
    }
    setEditRole(role);
    setFormName(role.name);
    setFormDesc(role.description ?? '');
    const codeSet = new Set(role.permissions.map((x) => x.permission.code));
    const menuKeys = (role.menuItems ?? []).map((m) => m.menuKey);
    /** 内置租户管理员：库中无 RoleMenuItem 时侧栏等价「不限制、与权限求交后全显」；表单展示为全选以免误解 */
    if (role.id === TENANT_ROLE_ADMIN_ID && menuKeys.length === 0) {
      setFormMenuPaths(new Set(menuCatalog.map((i) => i.path)));
    } else if (menuKeys.length === 0) {
      setFormMenuPaths(inferMenuPathsFromPermissionCodes(codeSet, menuCatalog));
    } else {
      setFormMenuPaths(new Set(menuKeys));
    }
  }

  function normalizeCodesWithDependencies(input: Set<string>) {
    const normalized = new Set(input);
    [...normalized].forEach((code) => {
      const dep = getViewDependency(code);
      if (dep) normalized.add(dep);
    });
    return normalized;
  }

  function autoRepairMenuConflicts(input: Set<string>) {
    const repaired = new Set(input);
    const repairedFrom: string[] = [];
    [...formMenuPaths].forEach((path) => {
      const item = menuCatalog.find((m) => m.path === path);
      if (!item || !item.permissions?.length) return;
      const hasAny = item.permissions.some((perm) => repaired.has(perm));
      if (hasAny) return;
      const pick = item.permissions.find((perm) =>
        defs.some((d) => d.code === perm),
      );
      if (pick) {
        repaired.add(pick);
        repairedFrom.push(path);
      }
    });
    return { repaired, repairedFrom };
  }

  function toggleMenuPath(path: string) {
    setFormMenuPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }

  async function submitCreate() {
    if (!canManage) return;
    const normalized = normalizeCodesWithDependencies(menuDrivenCodes);
    const { repaired, repairedFrom } = autoRepairMenuConflicts(normalized);
    const codes = [...repaired];
    const featurePermissionKeys = deriveFeaturePermissionKeys(codes);
    if (!formName.trim() || formMenuPaths.size === 0 || !codes.length) {
      toast.error('请填写名称并至少勾选一个菜单入口');
      return;
    }
    if (repairedFrom.length > 0) {
      toast.info(`已为 ${repairedFrom.length} 个菜单入口自动补齐可访问权限`);
    }
    try {
      setSaving(true);
      const { data: created } = await tenantRbacApi.createRole({
        name: formName.trim(),
        description: formDesc.trim() || undefined,
        featurePermissionKeys,
        permissionCodes: codes,
      });
      await tenantRbacApi.setRoleMenu(created.id, [...formMenuPaths]);
      toast.success('已创建角色');
      setCreateOpen(false);
      await load();
      await refreshSessionFromServer();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? '创建失败';
      toast.error(typeof msg === 'string' ? msg : '创建失败');
    } finally {
      setSaving(false);
    }
  }

  async function submitEdit() {
    if (!editRole || !canManage) return;
    const normalized = normalizeCodesWithDependencies(menuDrivenCodes);
    const { repaired, repairedFrom } = autoRepairMenuConflicts(normalized);
    const codes = [...repaired];
    const featurePermissionKeys = deriveFeaturePermissionKeys(codes);
    if (formMenuPaths.size === 0 || !codes.length) {
      toast.error('请至少勾选一个菜单入口');
      return;
    }
    if (repairedFrom.length > 0) {
      toast.info(`已为 ${repairedFrom.length} 个菜单入口自动补齐可访问权限`);
    }
    try {
      setSaving(true);
      const builtin = BUILTIN_LABELS.has(editRole.name);
      await tenantRbacApi.updateRole(editRole.id, {
        ...(!builtin ? { name: formName.trim() } : {}),
        description: formDesc.trim() || undefined,
        featurePermissionKeys,
        permissionCodes: codes,
      });
      await tenantRbacApi.setRoleMenu(editRole.id, [...formMenuPaths]);
      toast.success('已保存');
      setEditRole(null);
      await load();
      await refreshSessionFromServer();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? '保存失败';
      toast.error(typeof msg === 'string' ? msg : '保存失败');
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!deleteId || !canManage) return;
    try {
      await tenantRbacApi.deleteRole(deleteId);
      toast.success('已删除');
      setDeleteId(null);
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? '删除失败';
      toast.error(typeof msg === 'string' ? msg : '删除失败');
    }
  }

  function toggleRolePermissionExpand(roleId: string) {
    setExpandedRoleIds((prev) => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId);
      else next.add(roleId);
      return next;
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">角色与权限</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          在本租户内维护角色功能权限；内置「租户管理员」「租户成员」不可删除，可调整其权限。
          成员账号的角色在「成员与账号」中分配。
        </p>
      </div>

      {readOnly && (
        <div
          className={cn(
            'rounded-lg border px-4 py-3 text-sm',
            isLight
              ? 'border-cyan-300/40 bg-cyan-50 text-slate-700'
              : 'border-cyan-500/20 bg-cyan-500/5 text-zinc-300',
          )}
        >
          当前为<strong className={cn(isLight ? 'text-cyan-700' : 'text-cyan-200/90')}>只读</strong>
          ：当前账号仅可查看角色详情，不可新建、编辑或删除角色。
        </div>
      )}

      <Card className={cn(isLight ? 'border-slate-300 bg-white' : 'border-white/10 bg-zinc-900/40')}>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-lg">角色列表</CardTitle>
            <CardDescription>共 {roles.length} 个角色</CardDescription>
          </div>
          {canManage && (
            <Button
              type="button"
              className={cn(isLight ? lightPrimaryBtn : btnPrimaryNeon)}
              onClick={openCreate}
            >
              新建角色
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">加载中…</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className={cn(isLight ? 'border-slate-300 hover:bg-transparent' : 'border-white/10 hover:bg-transparent')}>
                  <TableHead>名称</TableHead>
                  <TableHead>类型</TableHead>
                  <TableHead>功能权限</TableHead>
                  <TableHead>成员数</TableHead>
                  {(canManage || readOnly) && (
                    <TableHead className="text-right">操作</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {roles.map((r) => {
                  const builtin = BUILTIN_LABELS.has(r.name);
                  const adminBuiltin = r.id === TENANT_ROLE_ADMIN_ID;
                  const featureLabels = deriveFeaturePermissionKeys(
                    r.permissions.map((x) => x.permission.code),
                  ).map((k) => FEATURE_PERMISSION_LABEL_MAP[k] ?? k);
                  const expanded = expandedRoleIds.has(r.id);
                  const summaryLabels = featureLabels.slice(0, 3);
                  const moreCount = Math.max(0, featureLabels.length - summaryLabels.length);
                  return (
                    <TableRow key={r.id} className={cn(isLight ? 'border-slate-300' : 'border-white/10')}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell>
                        {builtin ? (
                          <Badge
                            variant="outline"
                            className={cn(
                              isLight ? 'border-slate-300 text-slate-600' : 'border-white/15 text-zinc-300',
                            )}
                          >
                            内置
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className={cn(
                              isLight
                                ? 'border-cyan-300/45 text-cyan-700'
                                : 'border-cyan-500/30 text-cyan-200/90',
                            )}
                          >
                            自定义
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className={cn('max-w-md text-xs', isLight ? 'text-slate-500' : 'text-zinc-400')}>
                        {expanded ? (
                          <div className="flex flex-wrap items-center gap-1.5">
                            {featureLabels.map((label) => (
                              <span
                                key={label}
                                className={cn(
                                  'rounded-full border px-2 py-0.5',
                                  isLight
                                    ? 'border-cyan-300/45 bg-cyan-50 text-cyan-700'
                                    : 'border-cyan-500/30 bg-cyan-500/10 text-cyan-200/90',
                                )}
                              >
                                {label}
                              </span>
                            ))}
                            {featureLabels.length > 3 && (
                              <button
                                type="button"
                                className={cn(
                                  'rounded border px-2 py-0.5 text-[11px] transition-colors',
                                  isLight
                                    ? 'border-slate-300 text-slate-500 hover:border-cyan-400/45 hover:text-cyan-700'
                                    : 'border-white/15 text-zinc-500 hover:border-cyan-500/35 hover:text-cyan-200',
                                )}
                                onClick={() => toggleRolePermissionExpand(r.id)}
                              >
                                收起
                              </button>
                            )}
                          </div>
                        ) : summaryLabels.length > 0 ? (
                          <div className="flex flex-wrap items-center gap-1.5">
                            {summaryLabels.map((label) => (
                              <span
                                key={label}
                                className={cn(
                                  'rounded-full border px-2 py-0.5',
                                  isLight
                                    ? 'border-cyan-300/45 bg-cyan-50 text-cyan-700'
                                    : 'border-cyan-500/30 bg-cyan-500/10 text-cyan-200/90',
                                )}
                              >
                                {label}
                              </span>
                            ))}
                            {moreCount > 0 && (
                              <button
                                type="button"
                                className={cn(isLight ? 'text-slate-500' : 'text-zinc-500')}
                                onClick={() => toggleRolePermissionExpand(r.id)}
                              >
                                +{moreCount} 项
                              </button>
                            )}
                          </div>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{r._count.users}</TableCell>
                      {(canManage || readOnly) && (
                        <TableCell className="text-right">
                          {canManage ? (
                            <>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className={cn(isLight ? lightOutlineBtn : btnOutlineNeon)}
                                disabled={adminBuiltin}
                                title={adminBuiltin ? '内置租户管理员不可编辑' : undefined}
                                onClick={() => openEdit(r)}
                              >
                                编辑
                              </Button>
                              {!builtin && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className={cn('ml-2', isLight ? lightOutlineBtn : btnOutlineNeon)}
                                  disabled={r._count.users > 0}
                                  title={r._count.users > 0 ? '仍有成员绑定' : undefined}
                                  onClick={() => setDeleteId(r.id)}
                                >
                                  删除
                                </Button>
                              )}
                            </>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className={cn(isLight ? lightOutlineBtn : btnOutlineNeon)}
                              onClick={() => setViewRole(r)}
                            >
                              查看
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Drawer open={createOpen} onOpenChange={setCreateOpen} direction="right">
        <DrawerContent
          className={cn(
            adminDrawerShell('detail'),
            'p-0 [&>button]:!right-5 [&>button]:!top-5 [&>button]:rounded-xl [&>button]:border [&>button]:p-2 [&>button]:opacity-100',
            isLight
              ? 'border-slate-300 bg-white text-slate-900 [&>button]:border-slate-300 [&>button]:bg-white [&>button]:text-slate-500 [&>button]:hover:border-cyan-400/45 [&>button]:hover:bg-cyan-50 [&>button]:hover:text-cyan-700'
              : 'border-0 [&>button]:border-white/10 [&>button]:bg-zinc-900/90 [&>button]:text-zinc-400 [&>button]:hover:border-fuchsia-500/30 [&>button]:hover:bg-fuchsia-500/10 [&>button]:hover:text-fuchsia-200',
          )}
        >
          <DrawerHeader
            className={cn(
              adminDrawerHeaderClass,
              isLight ? 'border-b border-slate-300 bg-white' : 'border-b border-white/10',
            )}
          >
            <DrawerTitle className={cn(isLight ? 'text-slate-900' : 'text-zinc-50')}>
              新建角色
            </DrawerTitle>
            <DrawerDescription className={cn(isLight ? 'text-slate-500' : 'text-zinc-400')}>
              名称不可与内置角色重复。
            </DrawerDescription>
          </DrawerHeader>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4">
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="nr-name" className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>
                  名称
                </Label>
                <Input
                  id="nr-name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className={cn(drawerInputClass, isLight && lightDrawerField)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nr-desc" className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>
                  说明（可选）
                </Label>
                <Input
                  id="nr-desc"
                  value={formDesc}
                  onChange={(e) => setFormDesc(e.target.value)}
                  className={cn(drawerInputClass, isLight && lightDrawerField)}
                />
              </div>
              <div className="space-y-2">
                <Label className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>功能与导航权限</Label>
                <div
                  className={cn(
                    'rounded-md border px-3 py-2 text-xs',
                    isLight
                      ? 'border-cyan-300/40 bg-cyan-50 text-slate-600'
                      : 'border-cyan-500/20 bg-cyan-500/5 text-zinc-300',
                  )}
                >
                  已选 {permissionSummaryCount} 项功能权限
                  {manageWithoutViewCount > 0 &&
                    `，将自动补齐 ${manageWithoutViewCount} 项“查看”依赖`}
                  {menuConflictPaths.length > 0 &&
                    `，并修复 ${menuConflictPaths.length} 项菜单访问冲突`}
                  。
                </div>
                <p className={cn('text-xs', isLight ? 'text-slate-500' : 'text-zinc-500')}>
                  勾选菜单即自动授予对应功能权限；取消菜单会同步回收关联权限。
                </p>
                <div className={cn('mt-2 space-y-2 border-t pt-3', isLight ? 'border-slate-300' : 'border-white/10')}>
                  <p className={cn('text-xs font-medium', isLight ? 'text-slate-700' : 'text-zinc-300')}>
                    菜单入口（勾选即授权）
                  </p>
                  {menuConflictPaths.length > 0 && (
                    <p className={cn('text-xs', isLight ? 'text-amber-700' : 'text-amber-300')}>
                      检测到 {menuConflictPaths.length} 个菜单入口缺少可访问权限，保存时会自动补齐对应查看权限。
                    </p>
                  )}
                  {menuTreeForPicker ? (
                    <RoleMenuTreeCheckboxes
                      tree={menuTreeForPicker}
                      value={formMenuPaths}
                      onToggle={toggleMenuPath}
                      accent="cyan"
                    />
                  ) : (
                    <RoleMenuCheckboxes
                      items={menuCatalog}
                      value={formMenuPaths}
                      onToggle={toggleMenuPath}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <DrawerFooter
            className={cn(
              adminDrawerFooterClass,
              isLight ? 'border-slate-300 bg-white' : 'border-white/10',
            )}
          >
            <Button
              type="button"
              className={cn('w-full', isLight ? lightPrimaryBtn : btnPrimaryNeon)}
              disabled={saving}
              onClick={() => void submitCreate()}
            >
              {saving ? '提交中…' : '创建'}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <Drawer
        open={!!editRole}
        onOpenChange={(open) => {
          if (!open) setEditRole(null);
        }}
        direction="right"
      >
        <DrawerContent
          className={cn(
            adminDrawerShell('detail'),
            'p-0 [&>button]:!right-5 [&>button]:!top-5 [&>button]:rounded-xl [&>button]:border [&>button]:p-2 [&>button]:opacity-100',
            isLight
              ? 'border-slate-300 bg-white text-slate-900 [&>button]:border-slate-300 [&>button]:bg-white [&>button]:text-slate-500 [&>button]:hover:border-cyan-400/45 [&>button]:hover:bg-cyan-50 [&>button]:hover:text-cyan-700'
              : 'border-0 [&>button]:border-white/10 [&>button]:bg-zinc-900/90 [&>button]:text-zinc-400 [&>button]:hover:border-fuchsia-500/30 [&>button]:hover:bg-fuchsia-500/10 [&>button]:hover:text-fuchsia-200',
          )}
        >
          <DrawerHeader
            className={cn(
              adminDrawerHeaderClass,
              isLight ? 'border-b border-slate-300 bg-white' : 'border-b border-white/10',
            )}
          >
            <DrawerTitle className={cn(isLight ? 'text-slate-900' : 'text-zinc-50')}>
              编辑角色
            </DrawerTitle>
          </DrawerHeader>
          {editRole && (
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4">
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="er-name" className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>
                    名称
                  </Label>
                  <Input
                    id="er-name"
                    value={formName}
                    disabled={BUILTIN_LABELS.has(editRole.name)}
                    onChange={(e) => setFormName(e.target.value)}
                    className={cn(drawerInputClass, isLight && lightDrawerField)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="er-desc" className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>
                    说明（可选）
                  </Label>
                  <Input
                    id="er-desc"
                    value={formDesc}
                    onChange={(e) => setFormDesc(e.target.value)}
                    className={cn(drawerInputClass, isLight && lightDrawerField)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className={cn(drawerLabelClass, isLight && lightDrawerLabel)}>功能与导航权限</Label>
                  <div
                    className={cn(
                      'rounded-md border px-3 py-2 text-xs',
                      isLight
                        ? 'border-cyan-300/40 bg-cyan-50 text-slate-600'
                        : 'border-cyan-500/20 bg-cyan-500/5 text-zinc-300',
                    )}
                  >
                    已选 {permissionSummaryCount} 项功能权限
                    {manageWithoutViewCount > 0 &&
                      `，将自动补齐 ${manageWithoutViewCount} 项“查看”依赖`}
                    {menuConflictPaths.length > 0 &&
                      `，并修复 ${menuConflictPaths.length} 项菜单访问冲突`}
                    。
                  </div>
                  <p className={cn('text-xs', isLight ? 'text-slate-500' : 'text-zinc-500')}>
                    勾选菜单即自动授予对应功能权限；取消菜单会同步回收关联权限。
                  </p>
                  <div className={cn('mt-2 space-y-2 border-t pt-3', isLight ? 'border-slate-300' : 'border-white/10')}>
                    <p className={cn('text-xs font-medium', isLight ? 'text-slate-700' : 'text-zinc-300')}>
                      菜单入口（勾选即授权）
                    </p>
                    {menuConflictPaths.length > 0 && (
                      <p className={cn('text-xs', isLight ? 'text-amber-700' : 'text-amber-300')}>
                        检测到 {menuConflictPaths.length} 个菜单入口缺少可访问权限，保存时会自动补齐对应查看权限。
                      </p>
                    )}
                    {menuTreeForPicker ? (
                      <RoleMenuTreeCheckboxes
                        tree={menuTreeForPicker}
                        value={formMenuPaths}
                        onToggle={toggleMenuPath}
                        accent="cyan"
                      />
                    ) : (
                      <RoleMenuCheckboxes
                        items={menuCatalog}
                        value={formMenuPaths}
                        onToggle={toggleMenuPath}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          <DrawerFooter
            className={cn(
              adminDrawerFooterClass,
              isLight ? 'border-slate-300 bg-white' : 'border-white/10',
            )}
          >
            <Button
              type="button"
              className={cn('w-full', isLight ? lightPrimaryBtn : btnPrimaryNeon)}
              disabled={saving}
              onClick={() => void submitEdit()}
            >
              {saving ? '保存中…' : '保存'}
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <Drawer
        open={!!viewRole}
        onOpenChange={(open) => {
          if (!open) setViewRole(null);
        }}
        direction="right"
      >
        <DrawerContent
          className={cn(
            adminDrawerShell('narrow'),
            'p-0 [&>button]:!right-5 [&>button]:!top-5 [&>button]:rounded-xl [&>button]:border [&>button]:p-2 [&>button]:opacity-100',
            isLight
              ? 'border-slate-300 bg-white text-slate-900 [&>button]:border-slate-300 [&>button]:bg-white [&>button]:text-slate-500 [&>button]:hover:border-cyan-400/45 [&>button]:hover:bg-cyan-50 [&>button]:hover:text-cyan-700'
              : 'border-0 [&>button]:border-white/10 [&>button]:bg-zinc-900/90 [&>button]:text-zinc-400 [&>button]:hover:border-fuchsia-500/30 [&>button]:hover:bg-fuchsia-500/10 [&>button]:hover:text-fuchsia-200',
          )}
        >
          <DrawerHeader
            className={cn(
              adminDrawerHeaderClass,
              isLight ? 'border-b border-slate-300 bg-white' : 'border-b border-white/10',
            )}
          >
            <DrawerTitle className={cn(isLight ? 'text-slate-900' : 'text-zinc-50')}>
              查看角色
            </DrawerTitle>
            <DrawerDescription className={cn(isLight ? 'text-slate-500' : 'text-zinc-400')}>
              只读展示，不可修改。
            </DrawerDescription>
          </DrawerHeader>
          {viewRole && (
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-4 text-sm">
              <div className="grid gap-3">
                <div>
                  <span className={cn(isLight ? 'text-slate-500' : 'text-zinc-500')}>名称</span>
                  <p className="mt-1 font-medium text-foreground">{viewRole.name}</p>
                </div>
                {viewRole.description && (
                  <div>
                    <span className={cn(isLight ? 'text-slate-500' : 'text-zinc-500')}>说明</span>
                    <p className={cn('mt-1', isLight ? 'text-slate-700' : 'text-zinc-300')}>
                      {viewRole.description}
                    </p>
                  </div>
                )}
                <div>
                  <span className={cn(isLight ? 'text-slate-500' : 'text-zinc-500')}>功能权限</span>
                  {(() => {
                    const codes = viewRole.permissions.map((x) => x.permission.code);
                    const featureKeys = deriveFeaturePermissionKeys(codes);
                    const labels = featureKeys.map(
                      (k) => FEATURE_PERMISSION_LABEL_MAP[k] ?? k,
                    );
                    return (
                      <p className={cn('mt-1 text-xs leading-relaxed', isLight ? 'text-slate-600' : 'text-zinc-400')}>
                        {labels.join(' · ') || '—'}
                      </p>
                    );
                  })()}
                </div>
                <div>
                  <span className={cn(isLight ? 'text-slate-500' : 'text-zinc-500')}>
                    侧栏菜单（若已配置）
                  </span>
                  <p className={cn('mt-1 font-mono text-xs', isLight ? 'text-slate-600' : 'text-zinc-400')}>
                    {(viewRole.menuItems ?? []).length
                      ? viewRole.menuItems!.map((m) => m.menuKey).join(' · ')
                      : '—（未限制，仅按功能权限）'}
                  </p>
                </div>
              </div>
            </div>
          )}
          <DrawerFooter
            className={cn(
              adminDrawerFooterClass,
              isLight ? 'border-slate-300 bg-white' : 'border-white/10',
            )}
          >
            <Button
              type="button"
              className={cn('w-full', isLight ? lightPrimaryBtn : btnPrimaryNeon)}
              onClick={() => setViewRole(null)}
            >
              关闭
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent
          className={cn(
            isLight
              ? 'border-slate-300 bg-white text-slate-900'
              : 'border-white/10 bg-zinc-950 text-foreground',
          )}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>删除该角色？</AlertDialogTitle>
            <AlertDialogDescription className={cn(isLight ? 'text-slate-500' : 'text-zinc-400')}>
              仅当无成员绑定时可删除。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={cn(isLight ? lightOutlineBtn : btnOutlineNeon)}>
              取消
            </AlertDialogCancel>
            <AlertDialogAction
              className={cn(isLight ? lightPrimaryBtn : btnPrimaryNeon)}
              onClick={(e) => {
                e.preventDefault();
                void confirmDelete();
              }}
            >
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
